import { ROUTES } from "@/constants/Routes"
import { ATTRIBUTION_NAMES } from "@/constants/User"
import { hasAttribution } from "@/utils/permissions"
import { useUserOrganizationStore } from "@/store/useUserOrganizationStore"
import { useUserStore } from "@/store/useUserStore"
import {
  Alert,
  Box,
  Collapse,
  Divider,
  Link,
  Step,
  StepIcon,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material"
import Image from "next/image"
import application_assessments_example from "@/assets/images/application_assessments_example.png"
import application_details_example from "@/assets/images/application_details_example.png"
import { blueGrey } from "@mui/material/colors"
import styled from "@emotion/styled"
import theme from "@/styles/theme"
import { useState } from "react"
import { KeyboardArrowDownSharp } from "@mui/icons-material"

const CustomStepIcon = styled(StepIcon)(() => ({
  color: theme.palette.raip.main,
  fontWeight: 600,
  "&.Mui-active": {
    color: theme.palette.raip.main,
    fontWeight: 600,
  },
}))

const STEPS = ["Configurações", "Perguntas"]

export function ApplicationManagement() {
  const [expanded, setExpanded] = useState({
    configurations: false,
    questions: false,
  })

  const organizationId = useUserOrganizationStore(
    (store) => store.organization?.id,
  )

  const { userId, canAccess } = useUserStore(
    ({ id, attributions, isResponsible }) => ({
      userId: id,
      canAccess:
        (hasAttribution(attributions ?? [], ATTRIBUTION_NAMES.ORGANIZACAO) || isResponsible) &&
        organizationId &&
        id,
    }),
  )

  return (
    <>
      <Alert severity="warning">
        Somente responsáveis de organizações podem gerenciar aplicações
      </Alert>

      <Typography variant="body2">
        As aplicações são o{" "}
        <span style={{ fontWeight: 600 }}>pilar central</span> da plataforma.
        Constituem a base das avaliações na qual a metodologia RAIES é aplicada
        para fins de identificação do grau de impactos éticos, sociais e
        jurídicos, assim como disponibiliza orientações técnicas e
        organizacionais para adequação.
      </Typography>

      <Typography variant="body2">
        O fluxo de{" "}
        {canAccess ? (
          <Link href={`${ROUTES.ORGANIZATION.PROFILE}?userId=${userId}`}>
            cadastro
          </Link>
        ) : (
          "cadastro"
        )}{" "}
        de aplicação cumpre duas etapas:
      </Typography>

      <Stepper
        alternativeLabel
        sx={{ width: "100%", maxWidth: 260, marginInline: "auto" }}
      >
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel
              slotProps={{
                label: {
                  sx: {
                    marginTop: 0.8,
                    fontSize: "13px",
                    color: blueGrey[700],
                    userSelect: "none",
                  },
                },
              }}
              slots={{ stepIcon: CustomStepIcon, label: Typography }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              maxWidth: "100%",
              overflow: "hidden",
              gap: 1,
              cursor: "pointer",
            }}
            onClick={() =>
              setExpanded({
                ...expanded,
                configurations: !expanded.configurations,
              })
            }
          >
            <Typography variant="body2" fontWeight={600}>
              1. Configurações
            </Typography>

            <Divider sx={{ flex: 1, borderColor: blueGrey[100] }} />

            <KeyboardArrowDownSharp
              fontSize="small"
              sx={{
                color: blueGrey[500],
                width: "1.25rem",
                height: "1.25rem",
                transition: "transform 0.3s",
                transform: expanded.configurations
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </Box>

          <Collapse in={expanded.configurations} timeout={"auto"} unmountOnExit>
            <Box sx={{ paddingTop: 0.5, paddingInline: 2, paddingBottom: 1 }}>
              <Typography variant="body2" gutterBottom>
                Inserção dos dados básicos da aplicação:
              </Typography>

              <Box sx={{ display: "flex", width: "100%", gap: 6 }}>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "1.5rem",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <li>
                    <Typography variant="body2">Nome</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Descrição</Typography>
                  </li>
                </ul>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "1.5rem",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <li>
                    <Typography variant="body2">Área</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Público</Typography>
                  </li>
                </ul>
              </Box>
            </Box>
          </Collapse>
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              maxWidth: "100%",
              overflow: "hidden",
              gap: 1,
              cursor: "pointer",
            }}
            onClick={() =>
              setExpanded({
                ...expanded,
                questions: !expanded.questions,
              })
            }
          >
            <Typography variant="body2" fontWeight={600}>
              2. Perguntas
            </Typography>

            <Divider sx={{ flex: 1, borderColor: blueGrey[100] }} />

            <KeyboardArrowDownSharp
              fontSize="small"
              sx={{
                color: blueGrey[500],
                width: "1.25rem",
                height: "1.25rem",
                transition: "transform 0.3s",
                transform: expanded.questions
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </Box>

          <Collapse in={expanded.questions} timeout={"auto"} unmountOnExit>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                paddingTop: 0.5,
                paddingInline: 2,
                paddingBottom: 1,
              }}
            >
              <Typography variant="body2">
                Resolução das perguntas de enquadramento. Essas perguntas
                definem quais questionários AIAs a aplicação deverá responder
                conforme a metodologia RAIES.
              </Typography>

              <Box>
                <Image
                  src={application_assessments_example}
                  alt="Exemplo de registro de questionário AIA - etapa 2"
                  loading="lazy"
                  style={{
                    borderRadius: "0.25rem",
                    maxWidth: "100%",
                  }}
                  width={480}
                />
                <Typography
                  component={"p"}
                  variant="caption"
                  color={blueGrey[500]}
                  lineHeight={"0.9rem"}
                >
                  Cadastro de aplicação - Perguntas de enquadramento
                </Typography>
              </Box>

              <Typography variant="body2">
                Somente com todas as perguntas e campos anteriores preenchidos é
                que será possível cadastrar a aplicação.
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ marginTop: 0.5 }}>
        Aplicações podem ser cadastradas, editadas e excluídas do sistema. A
        exclusão é um processo{" "}
        <span style={{ fontWeight: 600 }}>irreversível</span>, portanto não é
        possivel recuperar uma aplicação excluída, assim como a exclusão remove
        todo o progresso dos questionários.
      </Typography>

      <Box>
        <Image
          src={application_details_example}
          alt="Visualização de aplicação"
          loading="lazy"
          style={{
            borderRadius: "0.25rem",
            maxWidth: "100%",
          }}
          width={360}
        />
        <Typography
          component={"p"}
          variant="caption"
          color={blueGrey[500]}
          lineHeight={"0.9rem"}
        >
          Visualização de aplicação com questionários respondidos e avaliação
        </Typography>
      </Box>

      <Box>
        <Typography variant="body2">
          O acompanhamento da aplicação é possível atraves da tabela de
          aplicações na mesma página de perfil da organização. O acompanhamento
          da aplicação permite visualizar e executar:
        </Typography>

        <Box sx={{ display: "flex", width: "100%", gap: 6 }}>
          <ul
            style={{
              margin: 0,
              paddingLeft: "1.5rem",
              paddingTop: "0.2rem",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <li>
              <Typography variant="body2">Dados básicos</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Progresso dos questionários
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Submissão para avaliação</Typography>
            </li>
            <ul
              style={{
                margin: 0,
                paddingLeft: "1.5rem",
                paddingTop: "0.2rem",
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <li>
                <Typography variant="body2" fontSize={"0.8rem"}>
                  Possível somente após responder todos os questionários (AIAs e
                  EPS)
                </Typography>
              </li>
            </ul>
            <li>
              <Typography variant="body2">Status da avaliação</Typography>
            </li>
            <li>
              <Typography variant="body2">Histórico de andamento</Typography>
            </li>
          </ul>
        </Box>
      </Box>
    </>
  )
}
